import * as React from 'react';
import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, getGroupedRowModel, getExpandedRowModel } from '@tanstack/react-table';
import type { ColumnDef, ColumnFiltersState, SortingState, VisibilityState, GroupingState, ExpandedState, Row, ColumnOrderState } from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ChevronDown,
  ChevronRight,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { FieldSelector } from '@/components/TimesheetReports/FieldSelector';
import type { FieldConfig, FieldPreset } from '@/components/TimesheetReports/FieldSelector';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { prefixKey } from '@/utils/storagePrefix';

interface DataTableEnhancedProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  searchKey?: string;
  searchPlaceholder?: string;
  enableGrouping?: boolean;
  enableColumnVisibility?: boolean;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  pageSize?: number;
  onRowClick?: (row: TData) => void;
  customFilters?: React.ReactNode;
  storageKey?: string;
  fieldLabels?: Record<string, string>; // Custom labels for fields
  fieldCategories?: Record<string, string>; // Categories for fields
  showSortIconsWhenNotSorted?: boolean; // Show sort icons even when column is not sorted
  toolbarActions?: React.ReactNode; // Custom actions to show in the toolbar
  onSelectionChange?: (selectedRows: TData[]) => void; // Callback when selection changes
  clearSelection?: boolean; // Trigger to clear selection
  hideFieldSelector?: boolean; // Hide the field selector from toolbar
  initialColumnVisibility?: VisibilityState; // Initial column visibility state
  initialGrouping?: GroupingState; // Initial grouping state
  filterChips?: React.ReactNode; // Active filter chips rendered below the toolbar row
}

export function DataTableEnhanced<TData, TValue>({
  columns,
  data,
  searchKey,
  searchPlaceholder = 'Search...',
  enableGrouping = true,
  enableColumnVisibility = true,
  enableSorting = true,
  enableFiltering = true,
  enableRowSelection = false,
  pageSize = 10,
  onRowClick,
  customFilters,
  storageKey,
  fieldLabels = {},
  fieldCategories = {},
  showSortIconsWhenNotSorted = true,
  toolbarActions,
  onSelectionChange,
  clearSelection = false,
  hideFieldSelector = false,
  initialColumnVisibility,
  initialGrouping,
  filterChips,
}: DataTableEnhancedProps<TData, TValue>) {
  // Load preferences from localStorage
  const loadPreferences = () => {
    if (!storageKey) return null;
    try {
      const saved = localStorage.getItem(prefixKey(`datatable-${storageKey}`));
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const savedPrefs = loadPreferences();

  const pinSpecialColumns = (order: string[]): string[] => {
    const filtered = order.filter(id => id !== 'select' && id !== 'actions');
    const allColumnIds = columns.map(c => ('accessorKey' in c ? String(c.accessorKey) : c.id) || '').filter(Boolean);
    const hasSelect = allColumnIds.includes('select') || columns.some(c => 'id' in c && c.id === 'select');
    const hasActions = allColumnIds.includes('actions') || columns.some(c => 'id' in c && c.id === 'actions');

    // Include any columns that exist in the definition but are missing from the saved order
    const missingColumns = allColumnIds.filter(id =>
      id !== 'select' && id !== 'actions' && !filtered.includes(id)
    );

    const result: string[] = [];
    if (hasSelect) result.push('select');
    result.push(...filtered, ...missingColumns);
    if (hasActions) result.push('actions');
    return result;
  };

  const [sorting, setSorting] = React.useState<SortingState>(savedPrefs?.sorting || []);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    savedPrefs?.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    initialColumnVisibility || savedPrefs?.columnVisibility || {}
  );
  const [grouping, setGrouping] = React.useState<GroupingState>(
    initialGrouping || savedPrefs?.grouping || []
  );
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(
    savedPrefs?.columnOrder ? pinSpecialColumns(savedPrefs.columnOrder) : []
  );

  // Field presets state
  const [fieldPresets, setFieldPresets] = React.useState<FieldPreset[]>(() => {
    if (!storageKey) return [];
    try {
      const saved = localStorage.getItem(prefixKey(`field-presets-${storageKey}`));
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Update columnVisibility when initialColumnVisibility changes
  React.useEffect(() => {
    if (initialColumnVisibility) {
      setColumnVisibility(initialColumnVisibility);
    }
  }, [initialColumnVisibility]);

  // Update grouping when initialGrouping changes
  React.useEffect(() => {
    if (initialGrouping) {
      setGrouping(initialGrouping);
    }
  }, [initialGrouping]);

  // Save preferences to localStorage
  React.useEffect(() => {
    if (!storageKey) return;
    const prefs = {
      sorting,
      columnFilters,
      columnVisibility,
      grouping,
      columnOrder: pinSpecialColumns(columnOrder),
    };
    localStorage.setItem(prefixKey(`datatable-${storageKey}`), JSON.stringify(prefs));
  }, [sorting, columnFilters, columnVisibility, grouping, columnOrder, storageKey]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    onColumnVisibilityChange: setColumnVisibility,
    onGroupingChange: setGrouping,
    getGroupedRowModel: enableGrouping ? getGroupedRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',
    onColumnOrderChange: (updater: ColumnOrderState | ((prev: ColumnOrderState) => ColumnOrderState)) => {
      setColumnOrder(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        return pinSpecialColumns(next);
      });
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: typeof enableRowSelection === 'function'
      ? (row: Row<TData>) => enableRowSelection(row)
      : enableRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      grouping,
      expanded,
      columnOrder,
      globalFilter,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  // Notify parent component when selection changes
  React.useEffect(() => {
    if (onSelectionChange && enableRowSelection) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map(row => row.original);
      onSelectionChange(selectedRows);
    }
  }, [rowSelection, onSelectionChange, enableRowSelection, table]);

  // Clear selection when clearSelection prop becomes true
  React.useEffect(() => {
    if (clearSelection && enableRowSelection) {
      setRowSelection({});
    }
  }, [clearSelection, enableRowSelection]);

  // Create field configs from columns
  const fieldConfigs: FieldConfig[] = React.useMemo(() => {
    const allCols = table
      .getAllColumns()
      .filter((column) => column.getCanHide());

    if (columnOrder.length > 0) {
      const orderMap = new Map(columnOrder.map((id, idx) => [id, idx]));
      allCols.sort((a, b) => {
        const aIdx = orderMap.get(a.id) ?? Infinity;
        const bIdx = orderMap.get(b.id) ?? Infinity;
        return aIdx - bIdx;
      });
    }

    return allCols.map((column, index) => ({
      id: column.id,
      label: fieldLabels[column.id] || column.id,
      visible: column.getIsVisible(),
      order: index,
      category: fieldCategories[column.id],
    }));
  }, [table, fieldLabels, fieldCategories, columnOrder]);

  // Compute default field configs using the original column definition order
  // (without any saved columnOrder applied). Used by FieldSelector to reset.
  const defaultFieldConfigs: FieldConfig[] = React.useMemo(() => {
    return table
      .getAllColumns()
      .filter((column) => column.getCanHide())
      .map((column, index) => ({
        id: column.id,
        label: fieldLabels[column.id] || column.id,
        visible: true,
        order: index,
        category: fieldCategories[column.id],
      }));
  }, [table, fieldLabels, fieldCategories]);

  const handleFieldsChange = (fields: FieldConfig[]) => {
    // Update column visibility based on field configs
    const newVisibility: VisibilityState = {};
    fields.forEach((field) => {
      newVisibility[field.id] = field.visible;
    });
    setColumnVisibility(newVisibility);

    const newOrder = pinSpecialColumns(fields.map((field) => field.id));
    setColumnOrder(newOrder);
  };

  const handleSavePreset = (preset: FieldPreset) => {
    const newPresets = [...fieldPresets, preset];
    setFieldPresets(newPresets);
    if (storageKey) {
      localStorage.setItem(prefixKey(`field-presets-${storageKey}`), JSON.stringify(newPresets));
    }
  };

  const handleDeletePreset = (presetId: string) => {
    const newPresets = fieldPresets.filter((p) => p.id !== presetId);
    setFieldPresets(newPresets);
    if (storageKey) {
      localStorage.setItem(prefixKey(`field-presets-${storageKey}`), JSON.stringify(newPresets));
    }
  };

  const resetPreferences = () => {
    setSorting([]);
    setColumnFilters([]);
    setColumnVisibility({});
    setGrouping([]);
    setColumnOrder([]);
    setGlobalFilter('');
    if (storageKey) {
      localStorage.removeItem(prefixKey(`datatable-${storageKey}`));
    }
  };

  const hasActiveFilters = columnFilters.length > 0 || globalFilter.length > 0;
  const hasActiveGrouping = grouping.length > 0;

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 shrink-0">
        <div className="flex flex-1 items-center gap-2">
          {/* Global Search */}
          {enableFiltering && searchKey && (
            <div className="relative flex-1 max-w-sm">
              <Input
                placeholder={searchPlaceholder}
                value={globalFilter ?? ''}
                onChange={(event) => setGlobalFilter(event.target.value)}
                className="pr-8"
              />
              {globalFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-2 hover:bg-transparent"
                  onClick={() => setGlobalFilter('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}

          {/* Custom Filters */}
          {customFilters}

          {/* Active Filters Badge */}
          {hasActiveFilters && (
            <Badge variant="secondary" className="gap-1">
              {columnFilters.length + (globalFilter ? 1 : 0)} filter(s)
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => {
                  setColumnFilters([]);
                  setGlobalFilter('');
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}


        </div>

        <div className="flex items-center gap-2">
          {/* Custom Toolbar Actions */}
          {toolbarActions}

          {/* Enhanced Field Selector */}
          {enableColumnVisibility && !hideFieldSelector && (
            <FieldSelector
              fields={fieldConfigs}
              defaultFields={defaultFieldConfigs}
              onFieldsChange={handleFieldsChange}
              storageKey={storageKey}
              presets={fieldPresets}
              onSavePreset={handleSavePreset}
              onDeletePreset={handleDeletePreset}
            />
          )}
        </div>
      </div>

      {/* Filter chips row — rendered below toolbar when provided */}
      {filterChips && (
        <div className="flex items-center gap-2 flex-wrap -mt-2 shrink-0">
          {filterChips}
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border overflow-auto flex-1 min-h-0">
        <Table className="text-xs">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <div className="flex items-center gap-2">
                          {enableSorting && header.column.getCanSort() ? (
                            <button
                              className="flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer font-semibold"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getIsSorted() && (
                                header.column.getIsSorted() === 'asc' ? (
                                  <ArrowUp className="h-3 w-3" />
                                ) : (
                                  <ArrowDown className="h-3 w-3" />
                                )
                              )}
                            </button>
                          ) : (
                            <span className="font-semibold">
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, rowIndex) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  {...(rowIndex === 0 ? { 'data-tour': 'approvals-first-row' } : {})}
                  className={cn(
                    onRowClick && 'cursor-pointer hover:bg-muted/50',
                    row.getIsGrouped() && 'bg-muted/30 font-medium'
                  )}
                  onClick={() => onRowClick && onRowClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {cell.getIsGrouped() ? (
                        // Grouped cell
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              row.getToggleExpandedHandler()();
                            }}
                          >
                            {row.getIsExpanded() ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())} (
                          {row.subRows.length})
                        </div>
                      ) : cell.getIsAggregated() ? (
                        // Aggregated cell
                        flexRender(
                          cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                          cell.getContext()
                        )
                      ) : cell.getIsPlaceholder() ? null : (
                        // Normal cell
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow data-tour="approvals-first-row">
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between shrink-0">
        <div className="text-sm text-muted-foreground">
          {(() => {
            const currentPage = table.getState().pagination.pageIndex;
            const pageSize = table.getState().pagination.pageSize;
            const totalRows = table.getFilteredRowModel().rows.length;
            
            if (totalRows === 0) {
              return 'No rows to display';
            }
            
            const startRow = currentPage * pageSize + 1;
            const endRow = Math.min((currentPage + 1) * pageSize, totalRows);
            
            return `Viewing rows ${startRow}-${endRow} of ${totalRows}`;
          })()}
        </div>
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => table.previousPage()}
                className={!table.getCanPreviousPage() ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            
            {(() => {
              const currentPage = table.getState().pagination.pageIndex + 1;
              const totalPages = table.getPageCount();
              const pages: (number | 'ellipsis')[] = [];
              
              if (totalPages <= 7) {
                // Show all pages if 7 or fewer
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(i);
                }
              } else {
                // Always show first page
                pages.push(1);
                
                if (currentPage > 3) {
                  pages.push('ellipsis');
                }
                
                // Show pages around current page
                const start = Math.max(2, currentPage - 1);
                const end = Math.min(totalPages - 1, currentPage + 1);
                
                for (let i = start; i <= end; i++) {
                  pages.push(i);
                }
                
                if (currentPage < totalPages - 2) {
                  pages.push('ellipsis');
                }
                
                // Always show last page
                pages.push(totalPages);
              }
              
              return pages.map((page, index) => {
                if (page === 'ellipsis') {
                  return (
                    <PaginationItem key={`ellipsis-${index}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }
                
                return (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => table.setPageIndex(page - 1)}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                );
              });
            })()}
            
            <PaginationItem>
              <PaginationNext
                onClick={() => table.nextPage()}
                className={!table.getCanNextPage() ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
}
